import { createAbortController, abortSignal, requestWasAborted, abortRequest } from "../../utils/browserCompat";
import { useEffect, useMemo, useState } from "react";
import { listProductCategories, listPublicProducts } from "../../api/products";
import { useAuth } from "../../context/AuthContext";
import { normalizeCategoryMetadata } from "../../utils/categoryAdapter";
import {
  getItemType,
  getProductCategory,
  getProductDescription,
  getProductId,
  getProductName,
} from "../../utils/productAdapter";
import { getProductImage } from "../../utils/productMedia";
import {
  catalogDetailPath,
  filterCatalogItems,
  normalizeCatalogMode,
  resolveCatalogModeFromSummary,
} from "../../utils/catalogRouting";

const PRODUCT_PREVIEW_LIMIT = 5;

function withQueries(nodes = [], hierarchy = "category-first") {
  if (hierarchy !== "subcategory-first") {
    return nodes.map((category) => ({
      ...category,
      query: { category: category.value },
      subcategories: (category.subcategories || []).map((subcategory) => ({
        ...subcategory,
        query: { category: category.value, subcategory: subcategory.value },
      })),
    }));
  }

  const grouped = new Map();
  nodes.forEach((category) => {
    (category.subcategories || []).forEach((subcategory) => {
      const key = String(subcategory.value || "").trim();
      if (!key) return;
      if (!grouped.has(key)) {
        grouped.set(key, {
          value: key,
          label: subcategory.label || key,
          count: 0,
          query: { subcategory: key },
          subcategories: [],
        });
      }
      const current = grouped.get(key);
      current.count += Number(subcategory.count || 0);
      current.subcategories.push({
        value: category.value,
        label: category.label,
        count: Number(subcategory.count || 0),
        query: { category: category.value, subcategory: key },
      });
    });
  });

  return Array.from(grouped.values())
    .map((subcategory) => ({
      ...subcategory,
      subcategories: subcategory.subcategories.sort(
        (a, b) => b.count - a.count || a.label.localeCompare(b.label, "es"),
      ),
    }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label, "es"));
}

export default function useNavbarCatalog({
  enabled,
  errorText,
  catalogMode,
  menuContent = "auto",
  hierarchy = "category-first",
}) {
  const { pricingRevision } = useAuth();
  const [categoryData, setCategoryData] = useState({ categories: [], summary: {} });
  const [previewItems, setPreviewItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [error, setError] = useState("");

  const configuredMode = ["products", "services", "mixed"].includes(menuContent)
    ? menuContent
    : catalogMode;

  useEffect(() => {
    if (!enabled) {
      setCategoryData({ categories: [], summary: {} });
      setPreviewItems([]);
      setLoading(false);
      setHasLoadedOnce(false);
      setError("");
      return undefined;
    }

    const controller = createAbortController();
    const loadCatalog = async () => {
      setLoading(true);
      setError("");

      try {
        const normalizedMode = normalizeCatalogMode(configuredMode);
        const requestedType = normalizedMode === "products"
          ? "product"
          : normalizedMode === "services"
            ? "service"
            : "";
        const facets = await listProductCategories({
          tipo: requestedType,
          signal: abortSignal(controller),
        });
        if (requestWasAborted(controller)) return;

        const resolved = resolveCatalogModeFromSummary(configuredMode, facets?.summary);
        const previewType = resolved === "products"
          ? "product"
          : resolved === "services"
            ? "service"
            : "";
        const preview = await listPublicProducts(
          {
            page: 1,
            pageSize: PRODUCT_PREVIEW_LIMIT,
            sortBy: "createdAt",
            sortDir: "desc",
            ...(previewType ? { tipo: previewType } : {}),
          },
          { signal: abortSignal(controller) },
        );
        if (requestWasAborted(controller)) return;

        setCategoryData(facets || { categories: [], summary: {} });
        setPreviewItems(Array.isArray(preview?.items) ? preview.items : []);
      } catch (requestError) {
        if (requestWasAborted(controller)) return;
        setError(
          errorText ||
            requestError?.message ||
            "No se pudieron cargar las categorías.",
        );
      } finally {
        if (!requestWasAborted(controller)) {
          setLoading(false);
          setHasLoadedOnce(true);
        }
      }
    };

    loadCatalog();
    return () => abortRequest(controller);
  }, [configuredMode, enabled, errorText, pricingRevision]);

  const resolvedMode = useMemo(
    () => resolveCatalogModeFromSummary(configuredMode, categoryData?.summary),
    [configuredMode, categoryData?.summary],
  );
  const products = useMemo(
    () => filterCatalogItems(previewItems, resolvedMode),
    [previewItems, resolvedMode],
  );

  const categories = useMemo(() => {
    const tree = normalizeCategoryMetadata(categoryData)
      .filter((category) => category.count > 0)
      .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label, "es"));
    return withQueries(tree, hierarchy);
  }, [categoryData, hierarchy]);

  const latestProducts = useMemo(
    () =>
      products
        .slice(0, PRODUCT_PREVIEW_LIMIT)
        .map((product) => ({
          id: getProductId(product),
          name: getProductName(product),
          description: getProductDescription(product),
          category: getProductCategory(product),
          itemType: getItemType(product),
          image: getProductImage(product, ""),
          path: catalogDetailPath(product),
        }))
        .filter((product) => product.id),
    [products],
  );

  const totalItems = Number(categoryData?.summary?.filteredTotal ?? categoryData?.summary?.total ?? 0);
  const hasProductItems = products.some((item) => getItemType(item) !== "service") ||
    (resolvedMode !== "services" && Number(categoryData?.summary?.productCount || 0) > 0);

  return {
    products,
    categories,
    latestProducts,
    resolvedMode,
    totalItems,
    hasProductItems,
    // Sólo el primer fetch puede ocupar el espacio con estado de carga.
    // Los refreshes posteriores conservan menú/categorías ya renderizados.
    loading: loading && !hasLoadedOnce,
    refreshing: loading && hasLoadedOnce,
    error,
  };
}
